import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Card, Typography, Spin, Alert, Button } from "antd";
import { CheckCircleOutlined, CloseCircleOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import axios from "axios";

const { Title, Text } = Typography;

function EmailConfirmation() {
  const [status, setStatus] = useState("loading"); // 'loading', 'success', 'error'
  const [message, setMessage] = useState("");
  const [error, setError] = useState(null);
  
  const location = useLocation();
  const navigate = useNavigate();
  
  const apiUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:8080/api";

  useEffect(() => {
    const confirmEmail = async () => {
      // Extract token from URL
      const searchParams = new URLSearchParams(location.search);
      const token = searchParams.get("token");

      if (!token) {
        setStatus("error");
        setError("Invalid confirmation link. Please check your email and try again.");
        return;
      }

      try {
        // Call backend confirmation endpoint
        const response = await axios.get(`${apiUrl}/auth/confirm?token=${token}`);
        
        setStatus("success");
        setMessage(response.data || "Your email has been successfully confirmed!");
      } catch (error) {
        console.error("Email confirmation failed:", error);
        setStatus("error");
        
        if (error.response?.data) {
          // Extract the message from the Spring Boot error response object
          const errorMessage = error.response.data.message || error.response.data.error || error.response.data;
          setError(errorMessage);
        } else if (error.response?.status === 400) {
          setError("This confirmation link is invalid or has expired.");
        } else if (error.response?.status === 404) {
          setError("Confirmation token not found. Please register again.");
        } else {
          setError("Unable to confirm your email. Please try again later.");
        }
      }
    };

    confirmEmail();
  }, [location, apiUrl]);

  // Auto-redirect after success (optional)
  useEffect(() => {
    let timer;
    if (status === "success") {
      timer = setTimeout(() => {
        navigate("/login");
      }, 5000); // Redirect after 5 seconds
    }
    return () => clearTimeout(timer);
  }, [status, navigate]);

  const handleRetry = () => {
    window.location.reload();
  };

  const handleGoToLogin = () => {
    navigate("/login");
  };

  const renderContent = () => {
    switch (status) {
      case "loading":
        return (
          <div style={{ textAlign: "center", padding: "40px 20px" }}>
            <Spin size="large" style={{ marginBottom: "16px" }} />
            <Title level={4} style={{ color: "#1890ff" }}>
              Confirming your email...
            </Title>
            <Text type="secondary">
              Please wait while we verify your email address.
            </Text>
          </div>
        );

      case "success":
        return (
          <div style={{ textAlign: "center", padding: "40px 20px" }}>
            <CheckCircleOutlined style={{ fontSize: "64px", color: "#52c41a", marginBottom: "16px" }} />
            <Title level={3} style={{ color: "#52c41a" }}>
              Email Confirmed Successfully!
            </Title>
            <Text style={{ fontSize: "16px", display: "block", marginBottom: "24px" }}>
              {message}
            </Text>
            <Text type="secondary" style={{ display: "block", marginBottom: "24px" }}>
              You will be automatically redirected to the login page in 5 seconds.
            </Text>
            <Button type="primary" size="large" onClick={handleGoToLogin}>
              Go to Login Now
            </Button>
          </div>
        );

      case "error":
        return (
          <div style={{ padding: "20px" }}>
            <Alert
              message="Confirmation Failed"
              description={error}
              type="error"
              showIcon
              icon={<CloseCircleOutlined />}
              style={{ marginBottom: "24px" }}
            />
            <div style={{ textAlign: "center" }}>
              <Button type="primary" onClick={handleRetry} style={{ marginRight: "8px" }}>
                Try Again
              </Button>
              <Button onClick={handleGoToLogin}>
                Go to Login
              </Button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div style={{ 
      display: "flex", 
      justifyContent: "center", 
      alignItems: "center", 
      minHeight: "60vh",
      padding: "20px"
    }}>
      <Card 
        style={{ 
          maxWidth: "500px", 
          width: "100%",
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          borderRadius: "8px"
        }}
        bodyStyle={{ padding: "40px" }}
      >
        {renderContent()}
      </Card>
    </div>
  );
}

export default EmailConfirmation;